import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { apiJson } from '@/lib/api';
import type { AdminUserDetailResponse } from '@/lib/types';

function copyText(text: string) {
  void navigator.clipboard.writeText(text).catch(() => {});
}

function formatQuietMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const period = hours >= 12 ? 'PM' : 'AM';
  const h12 = hours % 12 || 12;
  return `${h12}:${mins.toString().padStart(2, '0')} ${period}`;
}

function AdminUserOverviewPage() {
  const { userId } = Route.useParams();
  const id = Number(userId);
  const { getToken } = useAuth();

  const userQuery = useQuery({
    queryKey: ['admin', 'user-detail', id],
    queryFn: () => apiJson<AdminUserDetailResponse>(getToken, `/v1/admin/users/${id}`),
  });

  const d = userQuery.data;
  const onCopy = useCallback((t: string) => () => copyText(t), []);

  if (userQuery.isLoading || !d) {
    return (
      <div className='flex justify-center py-12'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  const ag = d.aggregates;

  return (
    <div className='space-y-6'>
      <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 grid gap-3 sm:grid-cols-2 text-sm'>
        <div>
          <span className='text-gray-500 text-xs font-semibold uppercase'>User ID</span>
          <div className='flex items-center gap-2 mt-1'>
            <code className='text-xs'>{d.id}</code>
            <button
              type='button'
              onClick={onCopy(String(d.id))}
              className='text-xs text-primary font-semibold hover:underline'
            >
              Copy
            </button>
          </div>
        </div>
        <div>
          <span className='text-gray-500 text-xs font-semibold uppercase'>Clerk ID</span>
          <div className='flex items-center gap-2 mt-1'>
            <code className='text-xs break-all'>{d.clerk_id ?? '—'}</code>
            {d.clerk_id ? (
              <button
                type='button'
                onClick={onCopy(d.clerk_id)}
                className='text-xs text-primary font-semibold shrink-0 hover:underline'
              >
                Copy
              </button>
            ) : null}
          </div>
        </div>
        <div>
          <span className='text-gray-500 text-xs font-semibold uppercase'>Created</span>
          <p className='mt-1'>{new Date(d.created_at).toLocaleString()}</p>
        </div>
        <div>
          <span className='text-gray-500 text-xs font-semibold uppercase'>Timezone</span>
          <p className='mt-1'>{d.timezone ?? '—'}</p>
        </div>
        <div>
          <span className='text-gray-500 text-xs font-semibold uppercase'>Date of birth</span>
          <p className='mt-1'>{d.date_of_birth ?? '—'}</p>
        </div>
        <div>
          <span className='text-gray-500 text-xs font-semibold uppercase'>Stripe status</span>
          <p className='mt-1'>{d.stripe_subscription_status ?? '—'}</p>
        </div>
        <div>
          <span className='text-gray-500 text-xs font-semibold uppercase'>RevenueCat (last event)</span>
          <p className='mt-1'>{d.revenuecat_subscription_status ?? '—'}</p>
        </div>
      </div>

      <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
        <h2 className='text-sm font-semibold text-gray-900 mb-2'>Calm Filter</h2>
        {!d.calm_filter_enabled ? (
          <p className='text-sm text-gray-500'>Not enabled — user has not turned on Calm Filter.</p>
        ) : (
          <dl className='grid sm:grid-cols-2 gap-3 text-sm'>
            <div>
              <dt className='text-gray-500 text-xs'>Status</dt>
              <dd className='mt-0.5'>
                <span className='inline-flex px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 text-xs font-semibold'>
                  Enabled
                </span>
                {d.calm_filter_active_now ? (
                  <span className='ml-2 inline-flex px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold'>
                    Quiet window active
                  </span>
                ) : null}
              </dd>
            </div>
            <div>
              <dt className='text-gray-500 text-xs'>Quiet hours (local)</dt>
              <dd className='mt-0.5'>
                {d.calm_filter_quiet_start_minutes != null &&
                d.calm_filter_quiet_end_minutes != null ? (
                  <>
                    {formatQuietMinutes(d.calm_filter_quiet_start_minutes)}
                    {' – '}
                    {formatQuietMinutes(d.calm_filter_quiet_end_minutes)}
                    {d.timezone ? (
                      <span className='text-gray-500 text-xs block mt-0.5'>
                        in {d.timezone}
                      </span>
                    ) : (
                      <span className='text-amber-700 text-xs block mt-0.5'>
                        Timezone not set — filter cannot run until timezone is set.
                      </span>
                    )}
                  </>
                ) : (
                  <span className='text-amber-700'>Quiet hours not configured</span>
                )}
              </dd>
            </div>
            {d.calm_filter_active_now && d.calm_filter_quiet_end_at_utc ? (
              <div>
                <dt className='text-gray-500 text-xs'>Quiet window ends (UTC)</dt>
                <dd className='mt-0.5 tabular-nums'>
                  {new Date(d.calm_filter_quiet_end_at_utc).toLocaleString()}
                </dd>
              </div>
            ) : null}
            <div>
              <dt className='text-gray-500 text-xs'>Last digest sent</dt>
              <dd className='mt-0.5'>
                {d.calm_filter_last_digest_at
                  ? new Date(d.calm_filter_last_digest_at).toLocaleString()
                  : '—'}
              </dd>
            </div>
          </dl>
        )}
      </div>

      <div>
        <h2 className='text-sm font-semibold text-gray-700 mb-2'>Activity counts</h2>
        <div className='grid grid-cols-2 sm:grid-cols-5 gap-2'>
          {(
            [
              ['Messages', ag.messages_authored],
              ['Expenses', ag.expenses_authored],
              ['Payments', ag.payments_authored],
              ['Journal', ag.journal_entries_authored],
              ['Check-ins', ag.check_ins_authored],
            ] as const
          ).map(([label, n]) => (
            <div
              key={label}
              className='bg-white rounded-lg border border-gray-200 p-3 text-center'
            >
              <p className='text-2xl font-bold text-gray-900'>{n}</p>
              <p className='text-xs text-gray-500 mt-1'>{label}</p>
            </div>
          ))}
        </div>
      </div>

      {d.attribution ? (
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
          <h2 className='text-sm font-semibold text-gray-900 mb-2'>Attribution</h2>
          <dl className='grid sm:grid-cols-2 gap-2 text-sm'>
            <div>
              <dt className='text-gray-500 text-xs'>Source</dt>
              <dd>{d.attribution.source ?? '—'}</dd>
            </div>
            <div>
              <dt className='text-gray-500 text-xs'>Medium / Campaign</dt>
              <dd>
                {[d.attribution.medium, d.attribution.campaign].filter(Boolean).join(' · ') || '—'}
              </dd>
            </div>
            <div>
              <dt className='text-gray-500 text-xs'>gclid</dt>
              <dd className='break-all'>{d.attribution.gclid ?? '—'}</dd>
            </div>
            <div>
              <dt className='text-gray-500 text-xs'>Captured</dt>
              <dd>
                {d.attribution.captured_at
                  ? new Date(d.attribution.captured_at).toLocaleString()
                  : '—'}
              </dd>
            </div>
          </dl>
        </div>
      ) : null}

      {d.onboarding ? (
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
          <h2 className='text-sm font-semibold text-gray-900 mb-2'>Onboarding</h2>
          <p className='text-xs text-gray-500 mb-2'>
            Updated{' '}
            {d.onboarding.completed_at
              ? new Date(d.onboarding.completed_at).toLocaleString()
              : '—'}
          </p>
          {d.onboarding.answers ? (
            <pre className='text-xs bg-gray-50 p-3 rounded-md overflow-x-auto'>
              {JSON.stringify(d.onboarding.answers, null, 2)}
            </pre>
          ) : (
            <p className='text-sm text-gray-500'>No answers stored.</p>
          )}
        </div>
      ) : null}

      {d.households.length > 0 ? (
        <div>
          <h2 className='text-sm font-semibold text-gray-700 mb-2'>Households</h2>
          <div className='space-y-3'>
            {d.households.map((h) => (
              <div
                key={h.household_id}
                className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-wrap gap-3 justify-between items-start'
              >
                <div>
                  <p className='font-semibold text-gray-900'>
                    Household #{h.household_id}
                    <button
                      type='button'
                      onClick={onCopy(String(h.household_id))}
                      className='ml-2 text-xs text-primary font-semibold hover:underline'
                    >
                      Copy ID
                    </button>
                  </p>
                  <p className='text-xs text-gray-500 mt-1'>
                    {h.message_count} messages · {h.expense_count} expenses
                  </p>
                  <p className='text-xs text-gray-500 mt-2'>
                    Members:{' '}
                    {h.members.map((m) => (
                      <span key={m.user_id} className='mr-2'>
                        {m.is_target ? '★ ' : ''}
                        {[m.first_name, m.last_name].filter(Boolean).join(' ') || `User ${m.user_id}`}
                      </span>
                    ))}
                  </p>
                  <div className='mt-3 pt-3 border-t border-gray-100'>
                    <p className='text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2'>
                      Beneficiaries
                    </p>
                    {(h.beneficiaries?.length ?? 0) === 0 ? (
                      <p className='text-xs text-gray-400'>None in this household.</p>
                    ) : (
                      <ul className='space-y-2'>
                        {(h.beneficiaries ?? []).map((b) => (
                          <li
                            key={b.id}
                            className='text-xs text-gray-700 bg-gray-50 rounded-md px-3 py-2 border border-gray-100'
                          >
                            <span className='font-semibold text-gray-900'>{b.name}</span>
                            <span className='text-gray-400'> · </span>
                            <span className='font-mono text-gray-600'>{b.type}</span>
                            <span className='text-gray-400'> · </span>
                            <span>ID {b.id}</span>
                            {b.birthdate ? (
                              <>
                                <span className='text-gray-400'> · </span>
                                <span className='tabular-nums'>DOB {b.birthdate}</span>
                              </>
                            ) : null}
                            <span className='text-gray-400'> · </span>
                            <span className='text-gray-500'>
                              added {new Date(b.created_at).toLocaleDateString()}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className='flex flex-wrap gap-2'>
                  <Link
                    to='/admin/users/$userId/chat'
                    params={{ userId }}
                    search={{ hid: String(h.household_id) }}
                    className='px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 hover:bg-gray-50'
                  >
                    Open chat
                  </Link>
                  <Link
                    to='/admin/users/$userId/expenses'
                    params={{ userId }}
                    search={{ hid: String(h.household_id) }}
                    className='px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 hover:bg-gray-50'
                  >
                    Open expenses
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className='text-sm text-gray-500'>No households.</p>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/users/$userId/')({
  validateSearch: (search: Record<string, unknown>) => ({
    hid: typeof search.hid === 'string' ? search.hid : undefined,
  }),
  component: AdminUserOverviewPage,
});
